/**
 * Campaigns domain — React Query hooks.
 *
 * Read hooks are hand-written wrappers around the generated client
 * functions, preserving null/404 behaviour from the legacy API layer.
 *
 * Write hooks are wrapped via `useInvalidatingMutation` so cache
 * invalidation is declarative and compile-time-enforced via the
 * `CampaignsMutationId` registry in `./invalidation`.
 */

import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import axios from 'axios'
import { endOfDay, startOfDay } from 'date-fns'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import { getDefaultCampaignLaunchReadiness } from './utils'
import type { CampaignSettings, CampaignSchedule } from './utils'

import {
  getCampaignSettingsQueryKey,
  getCampaignScheduleQueryKey,
  getCampaignStatsQueryKey,
  getCampaignInsightsQueryKey,
  getCampaignLaunchReadinessQueryKey,
} from '../generated/hooks/campaignsController'
import { getCampaignSettings } from '../generated/clients/getCampaignSettings'
import { getCampaignSchedule } from '../generated/clients/getCampaignSchedule'
import { getCampaignStats } from '../generated/clients/getCampaignStats'
import { getCampaignInsights } from '../generated/clients/getCampaignInsights'
import { getCampaignLaunchReadiness } from '../generated/clients/getCampaignLaunchReadiness'
import { updateCampaignSettings } from '../generated/clients/updateCampaignSettings'
import { updateCampaignSchedule } from '../generated/clients/updateCampaignSchedule'
import { launchCampaign } from '../generated/clients/launchCampaign'
import { pauseCampaign } from '../generated/clients/pauseCampaign'
import { resumeCampaign } from '../generated/clients/resumeCampaign'
import { scheduleCampaignLaunch } from '../generated/clients/scheduleCampaignLaunch'
import { cancelScheduledLaunch } from '../generated/clients/cancelScheduledLaunch'

import type { UpdateCampaignSettingsMutationRequest } from '../generated/types/UpdateCampaignSettings'
import type { UpdateCampaignScheduleMutationRequest } from '../generated/types/UpdateCampaignSchedule'
import type { ScheduleCampaignLaunchMutationRequest } from '../generated/types/ScheduleCampaignLaunch'
import type { GetCampaignStatsQueryParams } from '../generated/types/GetCampaignStats'

// Re-export generated query-key fns so consumers can invalidate by hand if needed.
export {
  getCampaignSettingsQueryKey,
  getCampaignScheduleQueryKey,
  getCampaignStatsQueryKey,
  getCampaignInsightsQueryKey,
  getCampaignLaunchReadinessQueryKey,
}

// ── READS (thin wrappers preserving old call signatures + null/404 behavior) ──

export const useGetCampaignSettings = (campaignId: number, enabled = true) =>
  useQuery({
    queryKey: getCampaignSettingsQueryKey({ campaign_id: campaignId }),
    queryFn: async () =>
      (await getCampaignSettings({
        campaign_id: campaignId,
      })) as CampaignSettings | null,
    enabled: enabled && campaignId > 0,
  })

export const useGetCampaignSchedule = (campaignId: number, enabled = true) =>
  useQuery({
    queryKey: getCampaignScheduleQueryKey({ campaign_id: campaignId }),
    queryFn: async () =>
      (await getCampaignSchedule({
        campaign_id: campaignId,
      })) as CampaignSchedule | null,
    enabled: enabled && campaignId > 0,
  })

export type CampaignStatsPeriod = 'week' | 'month' | 'all' | 'custom'

export interface CampaignStatsRange {
  from: Date
  to?: Date
}

/** Build the stats query params: a custom range maps to start/end, else period. */
function buildStatsParams(
  period: CampaignStatsPeriod,
  range?: CampaignStatsRange
): GetCampaignStatsQueryParams {
  if (period === 'custom' && range?.from) {
    return {
      start: startOfDay(range.from).toISOString(),
      end: endOfDay(range.to ?? range.from).toISOString(),
    }
  }
  return { period }
}

/**
 * Aggregate campaign stats for the stats bar, scoped to a time window. Keeps the
 * previous window's data visible while a new window loads (no flicker).
 */
export const useGetCampaignStats = (
  enabled = true,
  period: CampaignStatsPeriod = 'week',
  range?: CampaignStatsRange
) => {
  const params = buildStatsParams(period, range)
  return useQuery({
    queryKey: getCampaignStatsQueryKey(params),
    queryFn: ({ signal }) => getCampaignStats(params, { signal }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
  })
}

/**
 * Campaign insights, returning `null` (not throwing) when the campaign has no
 * linked EmailBison campaign yet (backend 404). Does not retry.
 */
export const useGetCampaignInsights = (campaignId: number, enabled = true) =>
  useQuery({
    queryKey: getCampaignInsightsQueryKey({ campaign_id: campaignId }),
    queryFn: async () => {
      try {
        return await getCampaignInsights({ campaign_id: campaignId })
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404)
          return null
        throw error
      }
    },
    enabled: enabled && campaignId > 0,
    retry: false,
  })

/**
 * Campaign launch readiness, falling back to an all-incomplete default (instead
 * of throwing) when the campaign is not yet linked to EmailBison (backend 404).
 */
export const useGetCampaignLaunchReadiness = (
  campaignId: number,
  enabled = true
) =>
  useQuery({
    queryKey: getCampaignLaunchReadinessQueryKey({ campaign_id: campaignId }),
    queryFn: async () => {
      try {
        return await getCampaignLaunchReadiness({ campaign_id: campaignId })
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return getDefaultCampaignLaunchReadiness(campaignId)
        }
        throw error
      }
    },
    enabled: enabled && campaignId > 0,
    retry: false,
  })

// ── MUTATIONS (ids thread through variables, matching the products precedent) ──

type UpdateSettingsVars = {
  campaign_id: number
  data: UpdateCampaignSettingsMutationRequest
}
type UpdateSettingsContext = {
  previous: unknown
  key: ReturnType<typeof getCampaignSettingsQueryKey>
}

/**
 * Update campaign settings with an optimistic cache write. The settings detail
 * query is snapshotted and patched in `onMutate`, rolled back in `onError`, and
 * reconciled by the invalidation registry on success.
 */
export function useUpdateCampaignSettings() {
  const queryClient = useQueryClient()
  return useInvalidatingMutation(
    'updateCampaignSettings',
    invalidationRegistry,
    {
      mutationFn: (vars: UpdateSettingsVars) =>
        updateCampaignSettings({ campaign_id: vars.campaign_id }, vars.data),
      onMutate: async (
        vars: UpdateSettingsVars
      ): Promise<UpdateSettingsContext> => {
        const key = getCampaignSettingsQueryKey({
          campaign_id: vars.campaign_id,
        })
        await queryClient.cancelQueries({ queryKey: key })
        const previous = queryClient.getQueryData(key)
        queryClient.setQueryData(
          key,
          (old: CampaignSettings | null | undefined) =>
            old ? { ...old, ...vars.data } : old
        )
        return { previous, key }
      },
      onError: (_err, _vars, context) => {
        const ctx = context as UpdateSettingsContext | undefined
        if (ctx?.previous !== undefined && ctx?.key) {
          queryClient.setQueryData(ctx.key, ctx.previous)
        }
      },
    }
  )
}

export function useUpdateCampaignSchedule() {
  const queryClient = useQueryClient()
  return useInvalidatingMutation(
    'updateCampaignSchedule',
    invalidationRegistry,
    {
      mutationFn: (vars: {
        campaign_id: number
        data: UpdateCampaignScheduleMutationRequest
      }) =>
        updateCampaignSchedule({ campaign_id: vars.campaign_id }, vars.data),
      onSuccess: (data, vars) => {
        queryClient.setQueryData(
          getCampaignScheduleQueryKey({ campaign_id: vars.campaign_id }),
          data
        )
      },
    }
  )
}

export function useLaunchCampaign() {
  return useInvalidatingMutation('launchCampaign', invalidationRegistry, {
    mutationFn: (vars: { bison_campaign_id: number; campaign_id?: number }) =>
      launchCampaign({ bison_campaign_id: vars.bison_campaign_id }),
  })
}

export function usePauseCampaign() {
  return useInvalidatingMutation('pauseCampaign', invalidationRegistry, {
    mutationFn: (vars: { bison_campaign_id: number; campaign_id?: number }) =>
      pauseCampaign({ bison_campaign_id: vars.bison_campaign_id }),
  })
}

export function useResumeCampaign() {
  return useInvalidatingMutation('resumeCampaign', invalidationRegistry, {
    mutationFn: (vars: { bison_campaign_id: number; campaign_id?: number }) =>
      resumeCampaign({ bison_campaign_id: vars.bison_campaign_id }),
  })
}

export function useScheduleCampaignLaunch() {
  return useInvalidatingMutation(
    'scheduleCampaignLaunch',
    invalidationRegistry,
    {
      mutationFn: (vars: {
        bison_campaign_id: number
        campaign_id?: number
        data: ScheduleCampaignLaunchMutationRequest
      }) =>
        scheduleCampaignLaunch(
          { bison_campaign_id: vars.bison_campaign_id },
          vars.data
        ),
    }
  )
}

export function useCancelScheduledLaunch() {
  return useInvalidatingMutation(
    'cancelScheduledLaunch',
    invalidationRegistry,
    {
      mutationFn: (vars: { bison_campaign_id: number; campaign_id?: number }) =>
        cancelScheduledLaunch({ bison_campaign_id: vars.bison_campaign_id }),
    }
  )
}

// type re-exports for consumers
export type {
  UpdateCampaignSettingsMutationRequest,
  UpdateCampaignScheduleMutationRequest,
  ScheduleCampaignLaunchMutationRequest,
}
